import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import ArticleCard from "@/components/feature/ArticleCard";
import { applySeo } from "@/utils/seo";
import { articles, getArticle } from "@/mocks/articles";
import type { Article } from "@/mocks/types";
import NotFound from "@/pages/NotFound";

export default function ArticleDetail() {
  const { id = "" } = useParams();
  const article: Article | undefined = getArticle(id);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!article) return;
    applySeo({
      title: `${article.title} | 灼见社`,
      description: `${article.deck} 独家内幕深度揭秘，${article.authorName}撰写。`,
      keywords: `最新爆料,独家内幕,深度揭秘,${article.tags.join(",")}`,
      path: `/article/${article.id}`,
      type: "article",
      jsonLd: [
        {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "首页", item: "https://yusr.com.cn/" },
            {
              "@type": "ListItem",
              position: 2,
              name: article.categoryName,
              item: `https://yusr.com.cn/channel/${article.categorySlug}`,
            },
            { "@type": "ListItem", position: 3, name: article.title },
          ],
        },
        {
          "@context": "https://schema.org",
          "@type": "NewsArticle",
          headline: article.title,
          description: article.deck,
          image: [article.cover],
          datePublished: article.publishDate,
          dateModified: article.publishDate,
          inLanguage: "zh-CN",
          author: { "@type": "Person", name: article.authorName },
          publisher: {
            "@type": "Organization",
            name: "灼见社",
            logo: { "@type": "ImageObject" },
          },
          articleSection: article.categoryName,
          keywords: article.tags.join(","),
          mainEntityOfPage: `https://yusr.com.cn/article/${article.id}`,
        },
      ],
    });
  }, [article, id]);

  const related = useMemo<Article[]>(() => {
    if (!article) return [];
    const same = articles.filter(
      (a) => a.categorySlug === article.categorySlug && a.id !== article.id
    );
    const others = articles.filter(
      (a) => a.categorySlug !== article.categorySlug && a.id !== article.id
    );
    return [...same, ...others].slice(0, 3);
  }, [article]);

  if (!article) return <NotFound />;

  async function share() {
    const url = typeof window !== "undefined" ? window.location.href : "";
    if (navigator.share) {
      try {
        await navigator.share({ title: article.title, url });
        return;
      } catch {
        /* ignore cancel */
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }

  function scrollToSection(i: number) {
    document.getElementById(`sec-${i}`)?.scrollIntoView({ behavior: "smooth" });
  }

  return (
    <>
      <SiteHeader />
      <main>
        <div className="mx-auto max-w-7xl px-4 pt-8 md:px-6">
          <nav
            aria-label="面包屑"
            className="flex flex-wrap items-center gap-1.5 text-sm text-foreground-500"
          >
            <Link to="/" className="hover:text-primary-600">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link
              to={`/channel/${article.categorySlug}`}
              className="hover:text-primary-600"
            >
              {article.categoryName}
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="line-clamp-1 text-foreground-700">{article.title}</span>
          </nav>

          <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-[1fr_220px]">
            <article>
              <header className="max-w-3xl">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center rounded bg-secondary-100 px-2 py-0.5 text-xs text-secondary-900">
                    {article.categoryName}
                  </span>
                  {article.breaking && (
                    <span className="inline-flex items-center gap-1 rounded bg-primary-100 px-2 py-0.5 text-xs text-primary-700">
                      <i className="ri-flashlight-line"></i>独家
                    </span>
                  )}
                  <span className="text-xs text-foreground-400">{article.publishDate}</span>
                </div>
                <h1 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-[32px]">
                  {article.title}
                </h1>
                <p className="mt-4 border-l-[3px] border-primary-500 pl-4 text-base leading-7 text-foreground-600">
                  {article.deck}
                </p>
                <div className="mt-5 flex flex-wrap items-center justify-between gap-4 border-y border-background-200/70 py-4">
                  <div className="flex items-center gap-3 text-sm text-foreground-700">
                    <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary-100 font-heading text-lg font-bold text-primary-700">
                      {article.authorInitial}
                    </span>
                    <span>
                      <span className="block font-medium text-foreground-900">
                        {article.authorName}
                      </span>
                      <span className="block text-xs text-foreground-500">
                        {article.authorRole} · {article.readMinutes} 分钟阅读
                      </span>
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-foreground-500">分享：</span>
                    <button
                      type="button"
                      aria-label="分享到微博"
                      onClick={share}
                      className="flex h-9 w-9 items-center justify-center rounded-full bg-background-100 text-foreground-700 transition-colors hover:bg-secondary-100 hover:text-secondary-900"
                    >
                      <i className="ri-weibo-line"></i>
                    </button>
                    <button
                      type="button"
                      aria-label="复制链接"
                      onClick={share}
                      className="flex h-9 w-9 items-center justify-center rounded-full bg-background-100 text-foreground-700 transition-colors hover:bg-secondary-100 hover:text-secondary-900"
                    >
                      <i className={copied ? "ri-check-line text-emerald-600" : "ri-link"}></i>
                    </button>
                  </div>
                </div>
              </header>

              <figure className="mt-6 overflow-hidden rounded-lg">
                <img
                  src={article.cover}
                  alt={article.alt}
                  className="max-h-[440px] w-full object-cover object-top"
                />
              </figure>

              <div className="mt-8 flex flex-col gap-6 lg:flex-row lg:gap-10">
                <div className="prose-custom max-w-none flex-1 text-[15px] leading-8 text-foreground-800">
                  <p className="text-base leading-8 text-foreground-700 first-letter:float-left first-letter:mr-2 first-letter:font-heading first-letter:text-5xl first-letter:font-black first-letter:leading-none first-letter:text-primary-600">
                    {article.deck}
                  </p>
                  {article.sections.map((s, i) => (
                    <section key={s.heading} id={`sec-${i}`} className="mt-8 scroll-mt-24">
                      <h2 className="mb-3 font-heading text-xl font-bold text-foreground-950">
                        {s.heading}
                      </h2>
                      {s.paragraphs.map((p, j) => (
                        <p key={j} className="mb-4 text-foreground-700">
                          {p}
                        </p>
                      ))}
                    </section>
                  ))}
                  <div className="mt-8 rounded-lg border border-background-200 bg-background-100 p-4 text-sm leading-6 text-foreground-600">
                    <strong className="text-foreground-800">采编说明：</strong>
                    本文基于多方公开信息与知情人士口径交叉梳理而成，人物与机构如涉及均指向行业现象，不作为针对具体对象的定性。内容仅供信息参考。
                  </div>
                </div>

                <aside className="shrink-0 lg:w-[220px]">
                  <div className="sticky top-20 rounded-lg border border-background-200/70 bg-background-100 p-4">
                    <p className="text-xs font-medium tracking-widest text-foreground-500">
                      本文目录
                    </p>
                    <ol className="mt-3 space-y-1">
                      {article.sections.map((s, i) => (
                        <li key={s.heading}>
                          <button
                            type="button"
                            onClick={() => scrollToSection(i)}
                            className="line-clamp-2 text-left text-sm text-foreground-700 transition-colors hover:text-primary-600"
                          >
                            {s.heading}
                          </button>
                        </li>
                      ))}
                    </ol>
                  </div>
                </aside>
              </div>
            </article>
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-2 border-t border-background-200/70 pt-5">
            {article.tags.map((t) => (
              <span
                key={t}
                className="rounded-full border border-background-200 bg-background-100 px-3 py-1 text-xs text-foreground-600"
              >
                #{t}
              </span>
            ))}
          </div>
        </div>

        {related.length > 0 && (
          <section className="mx-auto max-w-7xl px-4 pt-14 md:px-6">
            <div className="mb-5 flex items-center justify-between">
              <h2 className="font-heading text-2xl font-bold text-foreground-950">
                相关推荐
              </h2>
              <Link
                to={`/channel/${article.categorySlug}`}
                className="inline-flex items-center gap-1 whitespace-nowrap text-sm text-primary-600 hover:text-primary-700"
              >
                更多 {article.categoryName}
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((a) => (
                <ArticleCard key={a.id} article={a} showDeck />
              ))}
            </div>
          </section>
        )}
      </main>
      <SiteFooter />
    </>
  );
}